export const CRITICAL_LOCALSTORAGE_KEYS = [
  "deviceRegistrationId",
  "lastPurge",
  "versionReloadCount",
  "versionReloadTarget",
];

export function isCriticalKey(key: string): boolean {
  return CRITICAL_LOCALSTORAGE_KEYS.some((ck) => key.includes(ck));
}

/** Storage quota exceeded: check name and legacy DOMException code (22). */
export function isQuotaExceededError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "QuotaExceededError" || error.code === DOMException.QUOTA_EXCEEDED_ERR)
  );
}

/**
 * Browser eviction or backing-store failure signatures, minus quota (classified separately by
 * {@link isQuotaExceededError}): an `UnknownError` `DOMException` — what IndexedDB rejects with
 * when the backing store is evicted or broken — and Chromium's "opening backing store" /
 * "full disk" message variants. Mirrors the pre-rewrite classifier retired with release-259
 * (`db/storageCheck.ts`, WEB-2129 / WEB-2135); carried forward for WEB-2248.
 */
export function isEvictionClassStorageError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.name === "UnknownError" || /opening backing store|full disk/i.test(error.message))
  );
}

function backupCriticalKeys(storage: Storage = localStorage): Record<string, string> {
  const backup: Record<string, string> = {};
  for (const key of Object.keys(storage)) {
    if (isCriticalKey(key)) {
      const value = storage.getItem(key);
      if (value) backup[key] = value;
    }
  }
  return backup;
}

function restoreCriticalKeys(storage: Storage = localStorage, backup: Record<string, string>): void {
  for (const [key, value] of Object.entries(backup)) {
    try {
      storage.setItem(key, value);
    } catch {
      console.warn(`[localStorage] Could not restore ${key}`);
    }
  }
}

const WORKSPACE_KEY_SUFFIXES = [
  "-channel-sort-order",
  "-channel-filter-types",
  "-channel-sort-type",
  "-selectedFeedGroups",
  "-driverGridColumns",
  "-driverGridColumnOrder",
  "-driverGridColumnSizing",
  "-driverGridColumnsMigration",
];

/**
 * Remove workspace-scoped localStorage keys that don't belong to the current workspace.
 * This prevents unbounded key accumulation as users switch workspaces.
 */
export function cleanupStaleWorkspaceKeys(currentWorkspaceId: string): void {
  if (!currentWorkspaceId) return;
  for (const key of Object.keys(localStorage)) {
    const isWorkspaceScoped = WORKSPACE_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix));
    if (isWorkspaceScoped && !key.startsWith(currentWorkspaceId)) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * Set an item in storage with automatic quota-exceeded recovery.
 * On QuotaExceededError, progressively removes the largest non-critical keys,
 * then falls back to a full clear preserving only critical keys.
 */
export function safeSetItem(storage: Storage, key: string, serializedValue: string): void {
  try {
    storage.setItem(key, serializedValue);
  } catch (error) {
    if (isQuotaExceededError(error)) {
      console.warn(`[localStorage] QuotaExceededError for ${key}, attempting cleanup...`);
      recoverFromQuotaExceeded(storage, key, serializedValue);
    } else {
      console.error(`[localStorage] Error setting ${key}:`, error);
    }
  }
}

function recoverFromQuotaExceeded(storage: Storage, key: string, serializedValue: string): void {
  const allKeys = Object.keys(storage);

  const nonCriticalKeys = allKeys
    .filter((k) => !isCriticalKey(k) && k !== key)
    .map((k) => ({ key: k, size: (storage.getItem(k) ?? "").length }))
    .sort((a, b) => b.size - a.size);

  for (const entry of nonCriticalKeys) {
    storage.removeItem(entry.key);
    try {
      storage.setItem(key, serializedValue);
      console.info(`[localStorage] Set ${key} after removing ${entry.key}`);
      return;
    } catch {
      // Need to free more space
    }
  }

  console.warn(`[localStorage] Full clear required for ${key}`);
  const backup = backupCriticalKeys(storage);
  storage.clear();
  restoreCriticalKeys(storage, backup);

  try {
    storage.setItem(key, serializedValue);
    console.info(`[localStorage] Set ${key} after full clear`);
  } catch {
    console.error(`[localStorage] Cannot set ${key} even after full clear`);
  }
}

export function localStorageCheck(): void {
  try {
    const testKey = "__storage_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
  } catch (e) {
    if (isQuotaExceededError(e)) {
      console.warn("[STORAGE-QUOTA] localStorage is full, performing emergency cleanup...");

      const backup = backupCriticalKeys(localStorage);
      localStorage.clear();
      restoreCriticalKeys(localStorage, backup);

      console.info("[STORAGE-QUOTA] Emergency cleanup complete, app can now load");
    } else {
      console.error("[STORAGE-QUOTA] Unexpected localStorage error:", e);
    }
  }
}
